/**
 * @deprecated Scheduled for deletion in R-4 (resolver pipeline).
 * All data sources will resolve via `actionId` + `ActionRegistry` from JSON metadata.
 * Do NOT add new entries — use `actionId` in domain JSON instead.
 *
 * Active consumers: FormFieldAdapter.tsx, formProps.ts.
 * Removal blocked until form-engine resolves actionId strings (R-4, Task 043+).
 */
import type { FieldDef } from '../primitives/types';
import type { DataSource } from '@packages/ui/hook-form-adapters';

// Field primitives
import * as address from '../primitives/address';
import * as contact from '../primitives/contact';
import * as employment from '../primitives/employment';
import * as participant from '../primitives/participant';
import * as award from '../primitives/award';
import * as documentFields from '../primitives/document';

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

const registry = new Map<FieldDef, DataSource>([
  // ── Address ──────────────────────────────────────────────────────────────
  [
    address.country,
    {
      type: 'url',
      url: '/api/customer/api/app/country/countries',
      labelField: 'name',
      valueField: 'id',
      extraFields: { isOtherCountry: 'isOtherCountry', countryCallingCode: 'countryCallingCode' },
      pinFirst: { field: 'isOtherCountry', value: false },
    },
  ],
  [
    address.countryOfLegalResidency,
    {
      type: 'url',
      url: '/api/customer/api/app/country/countries',
      labelField: 'name',
      valueField: 'id',
      extraFields: { isOtherCountry: 'isOtherCountry' },
      pinFirst: { field: 'isOtherCountry', value: false },
    },
  ],
  [
    address.state,
    { type: 'url', url: '/api/customer/api/app/state/by-country/{countryId}', labelField: 'name', valueField: 'id' },
  ],

  // ── Contact ───────────────────────────────────────────────────────────────
  [
    contact.countryCallingCode,
    {
      type: 'url',
      url: '/api/customer/api/app/country/countries',
      labelField: 'name',
      valueField: 'id',
      extraFields: { isOtherCountry: 'isOtherCountry', countryCallingCode: 'countryCallingCode' },
      pinFirst: { field: 'isOtherCountry', value: false },
      autoDefault: true,
    },
  ],
  [
    contact.workNumberCode,
    {
      type: 'url',
      url: '/api/customer/api/app/country/countries',
      labelField: 'name',
      valueField: 'id',
      extraFields: { isOtherCountry: 'isOtherCountry', countryCallingCode: 'countryCallingCode' },
      pinFirst: { field: 'isOtherCountry', value: false },
      autoDefault: true,
    },
  ],

  // ── Employment ────────────────────────────────────────────────────────────
  [
    employment.employeeType,
    { type: 'url', url: '/api/customer/api/app/employee-type/employee-types', labelField: 'name', valueField: 'id' },
  ],
  [
    employment.division,
    { type: 'url', url: '/api/customer/api/app/customer-department/customer-departments', labelField: 'name', valueField: 'id' },
  ],
  [
    employment.jobTitle,
    { type: 'url', url: '/api/customer/api/app/customer-title/customer-titles', labelField: 'name', valueField: 'id' },
  ],
  [
    employment.settlementType,
    { type: 'url', url: '/api/award/api/app/option-exercise/settlement-types', labelField: 'displayName', valueField: 'id' },
  ],
  [
    employment.settlementMethods,
    { type: 'url', url: '/api/award/api/app/option-exercise/settlement-types', labelField: 'displayName', valueField: 'id' },
  ],

  // ── Participant ───────────────────────────────────────────────────────────
  [
    participant.transferAgentCompany,
    { type: 'url', url: '/api/customer/company/transfer-agent/current', labelField: 'name', valueField: 'id' },
  ],
  [
    participant.residentStatus,
    {
      type: 'url',
      url: '/api/customer/api/app/residence-status/residence-status-list',
      labelField: 'name',
      valueField: 'id',
      extraFields: { label: 'name' },
    },
  ],

  // ── Award ─────────────────────────────────────────────────────────────────
  [
    award.settlementTypeId,
    { type: 'url', url: '/api/award/api/app/option-exercise/settlement-types', labelField: 'name', valueField: 'id' },
  ],

  // ── Document / Templates ──────────────────────────────────────────────────
  [
    documentFields.grantAcceptanceTemplateId,
    { type: 'url', url: '/api/award/api/app/grant-acceptance-template/dropdown', labelField: 'name', valueField: 'id' },
  ],
]);

// ---------------------------------------------------------------------------
// Accessor — returns undefined if the field has no registered DataSource.
// formProps() calls this automatically for async-select fields, so a missing
// entry simply means the form must provide dataSource manually (e.g. for
// `type: 'fn'` or `type: 'custom'` data sources).
// ---------------------------------------------------------------------------

export function getDataSource(def: FieldDef): DataSource | undefined {
  return registry.get(def);
}
